/**
 * DailyTimingsPage — primary daily engagement surface.
 *
 * Shows today's Choghadiya timeline, inauspicious windows (Rahu Kaal,
 * Gulika, Yamaganda), and the Abhijit Muhurta highlight.
 *
 * Location: GPS auto-detect or city search via Nominatim.
 * Persisted to localStorage so it survives page reloads.
 *
 * Route: /daily-timings
 */
import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { choghadiyaApi, profileApi, type ChoghadiyaSlot } from '@/lib/api'
import { useUser, useAppStore } from '@/store'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import {
  MapPin, Navigation, Search, ChevronLeft, ChevronRight,
  Sun, Moon, Clock, AlertTriangle, Sparkles, RefreshCw, X,
} from 'lucide-react'
import { SkeletonTimeline, ErrorState } from '@/components/ui'
import { Card } from '@/components/Card'
import { Button } from '@/components/Button'

// ── Constants ─────────────────────────────────────────────────────────────────

const LOCATION_STORAGE_KEY = 'shubh-location'

const QUALITY_CONFIG: Record<ChoghadiyaSlot['quality'], {
  label: string
  bg: string
  text: string
  border: string
  dot: string
  auspicious: boolean
  meaning: string
  activities: string[]
}> = {
  Amrit: {
    label: 'Amrit', bg: 'bg-auspicious/10', text: 'text-auspicious', border: 'border-auspicious/30',
    dot: 'bg-auspicious', auspicious: true,
    meaning: 'Nectar — most auspicious period',
    activities: ['Marriage', 'New ventures', 'Important meetings', 'Medicine', 'Journeys'],
  },
  Shubh: {
    label: 'Shubh', bg: 'bg-auspicious/5', text: 'text-auspicious', border: 'border-auspicious/20',
    dot: 'bg-auspicious/70', auspicious: true,
    meaning: 'Auspicious — good for most activities',
    activities: ['Business deals', 'Education', 'Investments', 'Prayers'],
  },
  Labh: {
    label: 'Labh', bg: 'bg-gold/10', text: 'text-gold', border: 'border-gold/30',
    dot: 'bg-gold', auspicious: true,
    meaning: 'Labh — classically associated with financial and commercial activity',
    activities: ['Commerce', 'Shopping', 'Starting work', 'Learning'],
  },
  Char: {
    label: 'Char', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20',
    dot: 'bg-blue-400', auspicious: true,
    meaning: 'Movable — good for travel and movement',
    activities: ['Travel', 'Moving home', 'Vehicle purchase', 'Sports'],
  },
  Udveg: {
    label: 'Udveg', bg: 'bg-zinc-900/60', text: 'text-star-soft', border: 'border-zinc-800',
    dot: 'bg-star-muted', auspicious: false,
    meaning: 'Udveg — classically considered a cautionary period',
    activities: [],
  },
  Kaal: {
    label: 'Kaal', bg: 'bg-inauspicious/10', text: 'text-inauspicious', border: 'border-inauspicious/30',
    dot: 'bg-inauspicious', auspicious: false,
    meaning: 'Kaal — classically considered inauspicious for new beginnings',
    activities: [],
  },
  Rog: {
    label: 'Rog', bg: 'bg-inauspicious/10', text: 'text-inauspicious', border: 'border-inauspicious/20',
    dot: 'bg-inauspicious/70', auspicious: false,
    meaning: 'Rog — classically considered a low-scoring period',
    activities: [],
  },
}

// ── Location helpers ───────────────────────────────────────────────────────────

interface LocationState {
  lat: number
  lon: number
  cityName: string
  tz: string
}

const DEFAULT_LOCATION: LocationState = {
  lat: 28.6139,
  lon: 77.209,
  cityName: 'New Delhi',
  tz: 'Asia/Kolkata',
}

function loadSavedLocation(): LocationState | null {
  try {
    const raw = localStorage.getItem(LOCATION_STORAGE_KEY)
    if (!raw) return null
    const loc = JSON.parse(raw) as LocationState
    // Fix stale Etc/GMT-05 style timezones saved by a previous version
    if (!isValidTz(loc.tz)) {
      loc.tz = Intl.DateTimeFormat().resolvedOptions().timeZone
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(loc))
    }
    return loc
  } catch { /* ignore */ }
  return null
}

function saveLocation(loc: LocationState) {
  localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(loc))
}

// ── Nominatim city search ─────────────────────────────────────────────────────

interface NominatimResult {
  place_id: number
  display_name: string
  lat: string
  lon: string
  address?: { city?: string; town?: string; village?: string; state?: string; country?: string }
}

async function searchNominatim(query: string): Promise<NominatimResult[]> {
  const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=5`
  const res = await fetch(url, { headers: { 'Accept-Language': 'en' } })
  if (!res.ok) throw new Error('Nominatim search failed')
  return res.json() as Promise<NominatimResult[]>
}

function nominatimDisplayName(r: NominatimResult): string {
  const a = r.address ?? {}
  const city = a.city ?? a.town ?? a.village ?? ''
  const state = a.state ?? ''
  const country = a.country ?? ''
  if (city && country) return state ? `${city}, ${state}, ${country}` : `${city}, ${country}`
  return r.display_name.split(',').slice(0, 3).join(',').trim()
}

// Best-effort timezone for coordinates.
// Uses the browser's own timezone as a sensible default rather than the
// fragile Etc/GMT±N approach (which loses fractional offsets like India's +5:30).
function tzFromCoords(_lat: number, _lon: number, userTz?: string): string {
  if (userTz) return userTz
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// Validate that a timezone string is usable by the Intl API.
function isValidTz(tz: string): boolean {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: tz })
    return true
  } catch {
    return false
  }
}

// ── Abhijit window helper ─────────────────────────────────────────────────────

function getAbhijitWindow(sunrise: string, sunset: string) {
  const rise = dayjs(sunrise)
  const set  = dayjs(sunset)
  const mid  = rise.add(set.diff(rise) / 2)
  return {
    start: mid.subtract(24, 'minute'),
    end:   mid.add(24, 'minute'),
  }
}


// ── Sub-components ────────────────────────────────────────────────────────────

function SlotRow({
  slot, isActive, isExpanded, onClick,
}: {
  slot: ChoghadiyaSlot
  isActive: boolean
  isExpanded: boolean
  onClick: () => void
}) {
  const cfg   = QUALITY_CONFIG[slot.quality] ?? QUALITY_CONFIG.Udveg
  const start = dayjs(slot.startTime)
  const end   = dayjs(slot.endTime)

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      className={cn(
        'rounded-xl border transition-all cursor-pointer select-none group',
        'border-l-[3px]',
        cfg.bg, cfg.border,
        isActive && 'ring-1 ring-gold/40 shadow-card',
      )}
    >
      <div className="flex items-center gap-3 px-3 py-3">
        {/* Active pulse dot */}
        <div className="relative flex-shrink-0 w-2 h-2">
          <span className={cn('block w-2 h-2 rounded-full', cfg.dot)} />
          {isActive && (
            <span className={cn('absolute inset-0 rounded-full animate-ping opacity-60', cfg.dot)} />
          )}
        </div>

        {/* Time range */}
        <span className="font-mono text-2xs text-star-muted w-[88px] flex-shrink-0 tabular-nums">
          {start.format('HH:mm')}–{end.format('HH:mm')}
        </span>

        {/* Quality badge */}
        <span className={cn('font-semibold text-sm flex-1 group-hover:opacity-90 transition-opacity', cfg.text)}>
          {slot.quality}
        </span>

        {/* Planet */}
        <span className={cn('text-2xs font-mono px-1.5 py-0.5 rounded border', cfg.bg, cfg.border, cfg.text, 'opacity-70')}>
          {slot.planet}
        </span>

        {/* Period icon */}
        {slot.period === 'day'
          ? <Sun className="w-3.5 h-3.5 text-amber-400/60 flex-shrink-0" aria-label="Day" />
          : <Moon className="w-3.5 h-3.5 text-violet-glow/60 flex-shrink-0" aria-label="Night" />
        }

        {/* Active badge */}
        {isActive && (
          <span className="flex items-center gap-1 text-2xs text-gold font-mono flex-shrink-0 animate-pulse">
            <Clock className="w-3 h-3" aria-hidden />
            Now
          </span>
        )}
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-1 border-t border-cosmic-mist/30 space-y-3 animate-fade-in">
          <p className="text-star-soft text-sm leading-relaxed">{cfg.meaning}</p>
          {cfg.auspicious && cfg.activities.length > 0 && (
            <div>
              <p className="overline mb-2">Good for</p>
              <div className="flex flex-wrap gap-1.5">
                {cfg.activities.map((a) => (
                  <span
                    key={a}
                    className="px-2.5 py-0.5 rounded-full bg-cosmic-dusk border border-cosmic-mist/60 text-star-soft text-2xs font-medium"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          )}
          {!cfg.auspicious && (
            <p className="caption leading-relaxed">
              Avoid new beginnings, important contracts, and journeys during this period.
            </p>
          )}
          <p className="caption font-mono tabular-nums">
            Duration: {end.diff(start, 'minute')} min
          </p>
        </div>
      )}
    </div>
  )
}

function InauspiciousCard({ window: w }: { window: { name: string; start: string; end: string } }) {
  const now    = dayjs()
  const start  = dayjs(w.start)
  const end    = dayjs(w.end)
  const isNow  = now.isAfter(start) && now.isBefore(end)
  const coming = now.isBefore(start)
  const minutesAway = start.diff(now, 'minute')
  const hoursAway   = Math.floor(minutesAway / 60)
  const minsRem     = minutesAway % 60

  const inKaal = w.name === 'Rahu Kaal'

  return (
    <div className={cn(
      'flex items-start gap-3 p-3 rounded-xl border transition-all',
      isNow
        ? 'bg-inauspicious/15 border-inauspicious/40'
        : 'bg-zinc-900/60 border-zinc-800',
    )}>
      <div className={cn(
        'mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
        isNow ? 'bg-inauspicious/20 border border-inauspicious/30' : 'bg-zinc-800/60 border border-zinc-800',
      )}>
        <AlertTriangle className={cn('w-4 h-4', isNow ? 'text-inauspicious' : 'text-star-dim')} aria-hidden />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <p className={cn('text-sm font-semibold', isNow ? 'text-inauspicious' : 'text-star-bright')}>
            {w.name}
          </p>
          {isNow && (
            <span className="text-2xs font-mono text-inauspicious animate-pulse">● Active now</span>
          )}
        </div>
        <p className="text-star-dim text-2xs font-mono mt-0.5">
          {start.format('HH:mm')} – {end.format('HH:mm')}
          <span className="mx-1.5">·</span>
          {end.diff(start, 'minute')} min
        </p>
        {coming && minutesAway > 0 && minutesAway < 480 && (
          <p className="text-star-muted text-2xs mt-1">
            {hoursAway > 0
              ? `Starts in ${hoursAway}h ${minsRem}m`
              : `Starts in ${minutesAway} min`}
          </p>
        )}
        {inKaal && (
          <p className="text-star-dim text-2xs mt-1.5 leading-relaxed">
            Avoid contracts, surgery, and important travel. Routine tasks are fine.
          </p>
        )}
      </div>
    </div>
  )
}

function AbhijitCard({ sunrise, sunset }: { sunrise: string; sunset: string }) {
  const now = dayjs()
  const { start, end } = getAbhijitWindow(sunrise, sunset)
  const isActive = now.isAfter(start) && now.isBefore(end)
  const isPast   = now.isAfter(end)

  return (
    <div className={cn(
      'flex items-start gap-3 p-3 rounded-xl border transition-all',
      isActive
        ? 'bg-gold/15 border-gold/40'
        : isPast
          ? 'bg-zinc-900/60 border-zinc-800 opacity-60'
          : 'bg-gold/5 border-gold/20',
    )}>
      <div className={cn(
        'mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
        isActive ? 'bg-gold/20 border border-gold/30' : 'bg-zinc-800/60 border border-gold/20',
      )}>
        <Sparkles className={cn('w-4 h-4', isActive ? 'text-gold' : 'text-gold/60')} aria-hidden />
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <p className="text-sm font-semibold text-gold">Abhijit Muhurta</p>
          {isActive && <span className="text-2xs font-mono text-gold animate-pulse">● Active</span>}
          {isPast    && <span className="text-2xs font-mono text-star-dim">Passed</span>}
        </div>
        <p className="text-star-dim text-2xs font-mono mt-0.5">
          {start.format('HH:mm')} – {end.format('HH:mm')}
          <span className="mx-1.5">·</span>48 min
        </p>
        <p className="text-star-muted text-2xs mt-1.5 leading-relaxed">
          Universally auspicious midday window ruled by the Sun — ideal for any important activity.
        </p>
      </div>
    </div>
  )
}

// ── Location picker modal ─────────────────────────────────────────────────────

function LocationPicker({
  current,
  onSelect,
  onClose,
}: {
  current: LocationState
  onSelect: (loc: LocationState) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<NominatimResult[]>([])
  const [searching, setSearching] = useState(false)
  const [gpsStatus, setGpsStatus] = useState<'idle' | 'loading' | 'error'>('idle')
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced search
  useEffect(() => {
    if (query.length < 2) { setResults([]); return }
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await searchNominatim(query)
        setResults(r)
      } catch { /* ignore */ } finally {
        setSearching(false)
      }
    }, 400)
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current) }
  }, [query])

  function handleGPS() {
    if (!navigator.geolocation) { setGpsStatus('error'); return }
    setGpsStatus('loading')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: LocationState = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          cityName: `${pos.coords.latitude.toFixed(2)}°N, ${pos.coords.longitude.toFixed(2)}°E`,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
        // Try to reverse-geocode for a friendly name
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${loc.lat}&lon=${loc.lon}&format=json`, {
          headers: { 'Accept-Language': 'en' },
        })
          .then((r) => r.json())
          .then((data: NominatimResult) => {
            loc.cityName = nominatimDisplayName(data)
            onSelect(loc)
          })
          .catch(() => onSelect(loc))
        setGpsStatus('idle')
      },
      () => setGpsStatus('error'),
      { timeout: 8000 },
    )
  }

  function handleResultClick(r: NominatimResult) {
    const tz = tzFromCoords(Number(r.lat), Number(r.lon))
    onSelect({
      lat: Number(r.lat),
      lon: Number(r.lon),
      cityName: nominatimDisplayName(r),
      tz,
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 px-4 pb-4"
      role="dialog"
      aria-modal="true"
      aria-label="Location picker"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card-cosmic w-full max-w-md p-5 space-y-4 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-star-bright text-sm font-semibold">Set Location</h2>
            <p className="text-star-dim text-2xs font-mono mt-0.5">Current: {current.cityName}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-star-dim hover:text-star-soft transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* GPS button */}
        <button
          onClick={handleGPS}
          disabled={gpsStatus === 'loading'}
          className={cn(
            'w-full flex items-center gap-2.5 px-4 py-2.5 rounded-xl border transition-colors text-sm',
            'bg-violet/10 border-violet/30 text-violet-glow hover:bg-violet/20',
            gpsStatus === 'loading' && 'opacity-60 cursor-not-allowed',
            gpsStatus === 'error' && 'border-inauspicious/40 text-inauspicious bg-inauspicious/10',
          )}
        >
          {gpsStatus === 'loading'
            ? <RefreshCw className="w-4 h-4 animate-spin flex-shrink-0" />
            : <Navigation className="w-4 h-4 flex-shrink-0" />
          }
          {gpsStatus === 'loading' ? 'Detecting location…'
            : gpsStatus === 'error' ? 'GPS unavailable — try city search'
            : 'Use my current location (GPS)'}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-star-dim text-2xs font-mono">or search</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        {/* City search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-star-dim pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search city…"
            autoFocus
            className="input-cosmic w-full pl-9"
            aria-label="Search city"
          />
          {searching && (
            <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-star-dim animate-spin" />
          )}
        </div>

        {/* Results */}
        {results.length > 0 && (
          <ul className="space-y-1 max-h-52 overflow-y-auto">
            {results.map((r) => (
              <li key={r.place_id}>
                <button
                  onClick={() => handleResultClick(r)}
                  className="w-full flex items-start gap-2.5 px-3 py-2 rounded-lg text-left transition-colors hover:bg-zinc-800/60 border border-transparent hover:border-zinc-800"
                >
                  <MapPin className="w-3.5 h-3.5 text-star-dim mt-0.5 flex-shrink-0" />
                  <span className="text-star-soft text-sm leading-snug">{nominatimDisplayName(r)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {query.length >= 2 && !searching && results.length === 0 && (
          <p className="text-star-dim text-sm text-center py-2">No cities found</p>
        )}
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DailyTimingsPage() {
  const user = useUser()
  const setUser = useAppStore((s) => s.setUser)

  // ── Date navigation ──────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const isToday = selectedDate.isSame(dayjs(), 'day')

  // ── Location ─────────────────────────────────────────────────────────────────
  const [location, setLocation] = useState<LocationState>(() => {
    return loadSavedLocation() ?? {
      lat: user?.profile?.birthCity ? DEFAULT_LOCATION.lat : DEFAULT_LOCATION.lat,
      lon: DEFAULT_LOCATION.lon,
      cityName: DEFAULT_LOCATION.cityName,
      tz: user?.profile?.birthTimezone ?? DEFAULT_LOCATION.tz,
    }
  })
  const [showLocationPicker, setShowLocationPicker] = useState(false)

  function handleLocationSelect(loc: LocationState) {
    setLocation(loc)
    saveLocation(loc)
    setShowLocationPicker(false)
    // Persist to user profile (best-effort)
    profileApi.update({ birthLatitude: loc.lat, birthLongitude: loc.lon, birthTimezone: loc.tz })
      .then((updated) => setUser(updated))
      .catch(() => { /* non-critical */ })
  }

  // ── Data fetching ─────────────────────────────────────────────────────────────
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['daily-timings', selectedDate.format('YYYY-MM-DD'), location.lat, location.lon],
    queryFn: () => choghadiyaApi.getDailyTimings({
      date: selectedDate.format('YYYY-MM-DD'),
      lat:  location.lat,
      lon:  location.lon,
      tz:   location.tz,
    }),
    retry: 1,
    staleTime: 5 * 60 * 1000,
  })

  const timings = data

  // ── Active slot refresh every minute ─────────────────────────────────────────
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!isToday) return
    const id = setInterval(() => setTick((t) => t + 1), 60_000)
    return () => clearInterval(id)
  }, [isToday])

  // ── Auto-refresh at midnight ──────────────────────────────────────────────────
  useEffect(() => {
    const msUntilMidnight = dayjs().endOf('day').diff(dayjs()) + 1000
    const t = setTimeout(() => {
      if (isToday) {
        setSelectedDate(dayjs())
        refetch()
      }
    }, msUntilMidnight)
    return () => clearTimeout(t)
  }, [isToday, refetch])

  // ── Slot expansion ────────────────────────────────────────────────────────────
  const [expandedIdx, setExpandedIdx] = useState<string | null>(null)

  // ── Derived values ────────────────────────────────────────────────────────────
  const now = dayjs()
  const daySlots   = timings?.choghadiya.filter((s) => s.period === 'day') ?? []
  const nightSlots = timings?.choghadiya.filter((s) => s.period === 'night') ?? []

  const activeSlotKey = useCallback((slot: ChoghadiyaSlot) => slot.startTime, [])

  function isSlotActive(slot: ChoghadiyaSlot) {
    if (!isToday) return false
    // suppress lint warning: tick is used to force re-render every minute
    void tick
    return now.isAfter(dayjs(slot.startTime)) && now.isBefore(dayjs(slot.endTime))
  }

  const rahuKaal = timings?.inauspiciousWindows.find((w) =>
    w.name.toLowerCase().includes('rahu')
  )

  return (
    <div className="max-w-2xl space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="heading-cosmic text-xl font-semibold">Daily Timings</h1>
          <p className="text-star-muted text-sm mt-0.5 font-mono">चौघड़िया · Auspicious periods</p>
        </div>

        {/* Location button */}
        <Button
          onClick={() => setShowLocationPicker(true)}
          variant="secondary"
          size="sm"
          aria-label="Change location"
          className="flex items-center gap-1.5"
        >
          <MapPin className="w-3.5 h-3.5 text-gold flex-shrink-0" aria-hidden />
          <span className="truncate max-w-[180px]">{location.cityName}</span>
        </Button>
      </div>

      {/* Date navigation */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => setSelectedDate((d) => d.subtract(1, 'day'))}
          aria-label="Previous day"
          variant="ghost"
          size="sm"
          className="w-9 h-9 p-0"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        <div className="flex-1 text-center">
          <p className="text-star-bright text-sm font-semibold">
            {isToday ? 'Today' : selectedDate.format('ddd, D MMMM')}
          </p>
          <p className="text-star-dim text-2xs font-mono">{selectedDate.format('D MMMM YYYY')}</p>
        </div>

        <Button
          onClick={() => setSelectedDate((d) => d.add(1, 'day'))}
          aria-label="Next day"
          variant="ghost"
          size="sm"
          className="w-9 h-9 p-0"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>

        {!isToday && (
          <Button
            onClick={() => setSelectedDate(dayjs())}
            variant="primary"
            size="sm"
          >
            Today
          </Button>
        )}
      </div>

      {/* Sunrise/sunset strip */}
      {!isLoading && timings && (
        <Card className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sun className="w-4 h-4 text-amber-400" aria-hidden />
            <span className="text-star-soft text-sm font-mono tabular-nums">{dayjs(timings.sunrise).format('HH:mm')}</span>
            <span className="caption">Sunrise</span>
          </div>
          <div className="flex-1 h-px bg-gradient-to-r from-amber-400/20 via-gold/30 to-violet-glow/20 mx-4" />
          <div className="flex items-center gap-2">
            <span className="caption">Sunset</span>
            <span className="text-star-soft text-sm font-mono tabular-nums">{dayjs(timings.sunset).format('HH:mm')}</span>
            <Moon className="w-4 h-4 text-violet-glow/70" aria-hidden />
          </div>
        </Card>
      )}

      {/* Loading skeleton */}
      {isLoading && <SkeletonTimeline rows={8} />}

      {/* Error state */}
      {isError && (
        <ErrorState
          message="Could not load timings for the selected date."
          onRetry={refetch}
          compact
        />
      )}

      {/* ── Abhijit Muhurta ── */}
      {!isLoading && timings && (
        <section>
          <p className="overline mb-3">Abhijit Muhurta</p>
          <AbhijitCard sunrise={timings.sunrise} sunset={timings.sunset} />
        </section>
      )}

      {/* ── Inauspicious windows ── */}
      {!isLoading && timings && timings.inauspiciousWindows.length > 0 && (
        <section>
          <p className="overline mb-3">Inauspicious Periods</p>
          <div className="space-y-2">
            {timings.inauspiciousWindows.map((w) => (
              <InauspiciousCard key={w.name} window={w} />
            ))}
          </div>
        </section>
      )}

      {/* ── Day Choghadiya ── */}
      {!isLoading && daySlots.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Sun className="w-3.5 h-3.5 text-amber-400" aria-hidden />
            <p className="overline">
              Day periods
              {timings && (
                <span className="normal-case font-mono ml-1.5 text-star-dim tracking-normal">
                  · {dayjs(timings.sunrise).format('HH:mm')} – {dayjs(timings.sunset).format('HH:mm')}
                </span>
              )}
            </p>
          </div>
          <div className="space-y-2" role="list" aria-label="Day Choghadiya slots">
            {daySlots.map((slot) => {
              const key = activeSlotKey(slot)
              return (
                <SlotRow
                  key={key}
                  slot={slot}
                  isActive={isSlotActive(slot)}
                  isExpanded={expandedIdx === key}
                  onClick={() => setExpandedIdx(expandedIdx === key ? null : key)}
                />
              )
            })}
          </div>
        </section>
      )}

      {/* ── Night Choghadiya ── */}
      {!isLoading && nightSlots.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-3.5 h-3.5 text-violet-glow/70" aria-hidden />
            <p className="overline">
              Night periods
              {timings && (
                <span className="normal-case font-mono ml-1.5 text-star-dim tracking-normal">
                  · {dayjs(timings.sunset).format('HH:mm')} onwards
                </span>
              )}
            </p>
          </div>
          <div className="space-y-2" role="list" aria-label="Night Choghadiya slots">
            {nightSlots.map((slot) => {
              const key = activeSlotKey(slot)
              return (
                <SlotRow
                  key={key}
                  slot={slot}
                  isActive={isSlotActive(slot)}
                  isExpanded={expandedIdx === key}
                  onClick={() => setExpandedIdx(expandedIdx === key ? null : key)}
                />
              )
            })}
          </div>
        </section>
      )}

      {/* Footer note */}
      {!isLoading && (
        <p className="caption text-center font-mono leading-relaxed pt-2 border-t border-cosmic-mist/30">
          Calculated from {location.cityName}
          {rahuKaal && (
            <> · Rahu Kaal {dayjs(rahuKaal.start).format('HH:mm')}–{dayjs(rahuKaal.end).format('HH:mm')}</>
          )}
        </p>
      )}

      {/* Location picker modal */}
      {showLocationPicker && (
        <LocationPicker
          current={location}
          onSelect={handleLocationSelect}
          onClose={() => setShowLocationPicker(false)}
        />
      )}
    </div>
  )
}
